/**
 * Lectura del JWT sin verificar la firma.
 *
 * Solo se usa para recuperar el `userId` cuando la sesion existe a medias (ver
 * `AuthProvider`): la app Angular del subdominio guarda `token` en el mismo
 * origen pero no `user_id`. La verificacion real la hace el backend en cada
 * peticion, asi que decodificar aqui no debilita nada.
 */

/** Decodifica el payload de un JWT. Devuelve null si no es un JWT legible. */
export const decodeJwtPayload = (token?: string | null): Record<string, any> | null => {
  if (!token || typeof token !== 'string') return null;

  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    // base64url -> base64
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );

    return JSON.parse(json);
  } catch (error) {
    // Un token corrupto no debe romper la carga de la app.
    return null;
  }
};

/** `userId` interno del backend, tal como lo lee `jwtMiddleware`. */
export const decodeUserId = (token?: string | null): number | null => {
  const payload = decodeJwtPayload(token);
  const id = payload?.userId;
  return typeof id === 'number' ? id : null;
};

/** `true` si el token ya expiro o no es legible. */
export const isTokenExpired = (token?: string | null): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;
  return Date.now() >= payload.exp * 1000;
};
